import ReactDOM from 'react-dom/client'
import './index.css'
import App from './App'

import { MsalProvider } from '@azure/msal-react'
import { PublicClientApplication, EventType, type EventMessage, type AuthenticationResult, InteractionRequiredAuthError } from '@azure/msal-browser'
import { msalConfig } from './authConfig'
import { ApolloClient, InMemoryCache, ApolloProvider, createHttpLink } from '@apollo/client'
import { setContext } from '@apollo/client/link/context'
import { onError } from '@apollo/client/link/error'
import { startTokenRefreshMonitor } from './utility/tokenUtils'

import { LicenseManager } from 'ag-grid-enterprise'

LicenseManager.setLicenseKey(process.env.REACT_APP_AG_GRID_LICENSE_KEY as string)

export const msalInstance = new PublicClientApplication(msalConfig)

const resizeObserverLoopErr = () => {
  const resizeObserverErrDiv = document.createElement('div')
  const observer = new ResizeObserver(() => {
    try {
      resizeObserverErrDiv.innerHTML = 'ResizeObserver loop completed'
    } catch (e) {
      console.log('resize screen error', e)
    }
  })
  observer.observe(document.body)
}

resizeObserverLoopErr()

// Update the active account in the future to limit different user
const accounts = msalInstance.getAllAccounts()
if (accounts.length > 0) {
  msalInstance.setActiveAccount(accounts[0])
}

const accessTokenRequest = {
  scopes: [process.env.REACT_APP_EXPOSE_API as string],
  account: accounts[0]
}

// Modified to be synchronous and wait for token
const getAccessToken = async () => {
  try {
    const accessTokenResponse = await msalInstance.acquireTokenSilent(accessTokenRequest)
    const accessToken = accessTokenResponse.accessToken
    localStorage.setItem('token', accessToken)
    return accessToken
  } catch (error) {
    console.error('Failed to acquire token:', error)
    return null
  }
}

// Function to refresh token when it expires
const refreshAccessToken = async (): Promise<string> => {
  try {
    const account = msalInstance.getActiveAccount()
    if (!account) {
      throw new Error('No active account found')
    }

    const accessTokenResponse = await msalInstance.acquireTokenSilent({
      scopes: [process.env.REACT_APP_EXPOSE_API as string],
      account
    })
    const accessToken = accessTokenResponse.accessToken
    localStorage.setItem('token', accessToken)
    return accessToken
  } catch (error) {
    console.error('Failed to refresh token:', error)
    // If silent token acquisition fails, try interactive login
    if (error instanceof InteractionRequiredAuthError) {
      try {
        const account = msalInstance.getActiveAccount()
        if (account) {
          await msalInstance.acquireTokenRedirect({
            scopes: [process.env.REACT_APP_EXPOSE_API as string],
            account
          })
          // Note: acquireTokenRedirect doesn't return a response, it redirects
          // The token will be available after the redirect
          throw new Error('Redirect required for token acquisition')
        }
      } catch (redirectError) {
        console.error('Failed to acquire token via redirect:', redirectError)
      }
    }
    throw error
  } finally {
    // Clear the token from localStorage when we're done
    localStorage.removeItem('token')
  }
}

// Wait for token before rendering app
const initializeApp = async () => {
  await getAccessToken()

  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement
  )

  const authLink = setContext(async (_, { headers }) => {
    // Use proactive token refresh to ensure we always have a valid token
    const { refreshTokenIfNeeded } = await import('./utility/tokenUtils')
    const token = await refreshTokenIfNeeded()
    return {
      headers: {
        ...headers,
        Authorization: token ? `Bearer ${token}` : ''
      }
    }
  })

  // Error link to handle 401 errors and token refresh
  const errorLink = onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach(({ message, locations, path }) => {
        console.error(
          `[GraphQL error]: Message: ${message}, Location: ${locations?.join(', ') ?? 'unknown'}, Path: ${path?.join('.') ?? 'unknown'}`
        )
      })
    }

    if (networkError) {
      console.error(`[Network error]: ${networkError.message || String(networkError)}`)

      // Check if it's a 401 error (unauthorized)
      if ('statusCode' in networkError && networkError.statusCode === 401) {
        console.log('Token expired, attempting to refresh...')

        // Handle token refresh and retry
        refreshAccessToken()
          .then(newToken => {
            // Update the operation context with the new token
            const oldHeaders = operation.getContext().headers
            operation.setContext({
              headers: {
                ...oldHeaders,
                Authorization: `Bearer ${newToken}`
              }
            })

            // Retry the operation with the new token
            forward(operation)
          })
          .catch(error => {
            console.error('Failed to refresh token:', error)
            // If refresh fails, redirect to login
            window.location.reload()
          })
      }
    }
  })

  const httpLink = createHttpLink({
    uri: process.env.REACT_APP_API_URL,
    // Extend timeout to 60 seconds (default is usually 10-30 seconds)
    fetchOptions: {
      timeout: 60000 // 60 seconds
    }
  })

  const client = new ApolloClient({
    link: errorLink.concat(authLink.concat(httpLink)),
    cache: new InMemoryCache(),
    defaultOptions: {
      watchQuery: {
        errorPolicy: 'all',
        notifyOnNetworkStatusChange: true,
        // Extend timeout for watch queries
        fetchPolicy: 'network-only',
        nextFetchPolicy: 'cache-first'
      },
      query: {
        errorPolicy: 'all',
        // Extend timeout for regular queries
        fetchPolicy: 'network-only'
      },
      mutate: {
        errorPolicy: 'all'
      }
    }
  })

  // Start proactive token refresh monitoring
  startTokenRefreshMonitor()

  root.render(
    <MsalProvider instance={msalInstance}>
      <ApolloProvider client={client}>
        <App />
      </ApolloProvider>
    </MsalProvider>
  )
}

// Initialize the app
initializeApp()

msalInstance.addEventCallback((event: EventMessage) => {
  if (event.eventType === EventType.LOGIN_SUCCESS && (event.payload != null)) {
    const payload = event.payload as AuthenticationResult
    const account = payload.account
    if (account) {
      msalInstance.setActiveAccount(account)
    }
  } else if (event.eventType === EventType.SSO_SILENT_SUCCESS && (event.payload != null)) {
    const payload = event.payload as AuthenticationResult
    const account = payload.account
    if (account) {
      msalInstance.setActiveAccount(account)
    }
  } else if (event.eventType === EventType.ACQUIRE_TOKEN_SUCCESS && (event.payload != null)) {
    const payload = event.payload as AuthenticationResult
    const account = payload.account
    if (account && !msalInstance.getActiveAccount()) {
      msalInstance.setActiveAccount(account)
    }
  }
})
